import addZeroPrefix from 'helpers/addZeroPrefix/addZeroPrefix';

import { parseRelatedItemForFE } from '../selfTests/parsers';

import { TimePeriod, YearPlannerActivityInput } from './yearPlannerActivities.types';
import type {
  OrganizationYearPlannerActivityTemplate,
  OrganizationYearPlannerActivityTemplateDTO,
  YearPlannerActivityDetails,
  YearPlannerActivityDetailsDTO,
  YearPlannerActivity,
  YearPlannerActivityDTO,
  YearPlannerActivityInputDTO,
} from './yearPlannerActivities.types';

const parseDateForFE = (date: string): TimePeriod => {
  const [month, day] = date.split('-');
  return { month: +month, day: +day };
};

export const formatTimePeriodToString = ({ month, day }: TimePeriod) => `${addZeroPrefix(month)}-${addZeroPrefix(day)}`;

const parseDateForBE = ({ month, day }: TimePeriod): string => `${addZeroPrefix(month + 1)}-${addZeroPrefix(day)}`;

const parseCategoryForFE = ({ id, name, name_translation_key }: YearPlannerActivityDTO['categories'][number]) => ({
  id,
  name,
  nameTranslationKey: name_translation_key,
});

export const parseOrganizationYearPlannerActivityTemplateForFE = (
  raw: OrganizationYearPlannerActivityTemplateDTO,
): OrganizationYearPlannerActivityTemplate => ({
  id: raw.id,
  name: raw.name,
  description: raw.description,
  createdAt: raw.created_at,
  updatedAt: raw.updated_at,
  isDeleted: raw.is_deleted,
  deletedAt: raw.deleted_at,
});

export const parseSingleYearPlannerActivityForFE = (raw: YearPlannerActivityDetailsDTO): YearPlannerActivityDetails => ({
  id: raw.id,
  begin: parseDateForFE(raw.begin),
  end: parseDateForFE(raw.end),
  activity: raw.activity
    ? {
        id: raw.activity.id,
        name: raw.activity.name,
        nameTranslationKey: raw.activity.name_translation_key,
        description: raw.activity.description,
        descriptionTranslationKey: raw.activity.description_translation_key,
        relatedArticles: raw.activity.related_articles.map(parseRelatedItemForFE),
        relatedGuides: raw.activity.related_guides.map(parseRelatedItemForFE),
        relatedSelfTests: raw.activity.related_self_tests.map(parseRelatedItemForFE),
        templates: raw.activity.templates,
      }
    : null,
  activitySource: raw.activity_source,
  displayName: raw.display_name,
  displayDescription: raw.display_description,
  organizationActivityTemplate: raw.organization_activity_template
    ? parseOrganizationYearPlannerActivityTemplateForFE(raw.organization_activity_template)
    : null,
  categories: raw.categories.map(parseCategoryForFE),
  companyNotes: raw.company_notes,
});

export const parseAllYearPlannerActivityForFE = (raw: YearPlannerActivityDTO[]): YearPlannerActivity[] =>
  raw.map(
    ({ id, activity, activity_source, display_name, display_description, organization_activity_template, end, begin, categories }) => ({
      id,
      end: parseDateForFE(end),
      begin: parseDateForFE(begin),
      activity: activity
        ? {
            id: activity.id,
            description: activity.description,
            descriptionTranslationKey: activity.description_translation_key,
            name: activity.name,
            nameTranslationKey: activity.name_translation_key,
          }
        : null,
      activitySource: activity_source,
      displayName: display_name || activity?.name || '',
      displayDescription: display_description || activity?.description || '',
      organizationActivityTemplate: organization_activity_template
        ? parseOrganizationYearPlannerActivityTemplateForFE(organization_activity_template)
        : null,
      categories: (categories || []).map(parseCategoryForFE),
    }),
  );

export const parseYearPlannerActivityCreateInputForBE = (formData: YearPlannerActivityInput): YearPlannerActivityInputDTO => {
  const payload: YearPlannerActivityInputDTO = {
    end: parseDateForBE(formData.end),
    begin: parseDateForBE(formData.begin),
    company_notes: formData.companyNotes || null,
    categories_ids: Object.entries(formData.categoriesIds)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .filter(([_, value]) => !!value)
      .map(([key]) => key),
  };

  if (formData.activitySource === 'pocketsafe' && formData.activityId) {
    payload.activity_id = formData.activityId;
  }
  if (
    formData.activitySource === 'organization' &&
    formData.organizationActivityTemplateId &&
    !formData.preserveDeletedOrganizationTemplateReference
  ) {
    payload.organization_activity_template_id = formData.organizationActivityTemplateId;
  }

  return payload;
};
